import React, { forwardRef, useState } from 'react';
import { Lock, Eye, EyeOff } from 'lucide-react';

/**
 * PasswordInput — an <input> with a left lock glyph, a right eye toggle,
 * and consistent dark-theme styling used by every password field in the app.
 * Accepts all native input props (value, onChange, autoComplete, placeholder,
 * required, minLength, name, id, ...) and forwards the ref.
 */
const PasswordInput = forwardRef(function PasswordInput(
  { className = '', 'data-testid': testId, iconLeft = true, ...props },
  ref
) {
  const [visible, setVisible] = useState(false);
  const Icon = visible ? EyeOff : Eye;

  return (
    <div className="relative">
      {iconLeft && (
        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      )}
      <input
        ref={ref}
        {...props}
        type={visible ? 'text' : 'password'}
        data-testid={testId}
        className={
          `w-full h-11 ${iconLeft ? 'pl-9' : 'pl-3'} pr-11 rounded-lg bg-background border border-border ` +
          `focus:border-primary focus:outline-none text-sm ${className}`
        }
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        title={visible ? 'Hide password' : 'Show password'}
        tabIndex={-1}
        data-testid={testId ? `${testId}-toggle` : 'password-toggle'}
        className="absolute right-1 top-1/2 -translate-y-1/2 h-9 w-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/60 transition-colors"
      >
        <Icon className="w-4 h-4" strokeWidth={2} />
      </button>
    </div>
  );
});

export default PasswordInput;
